"use client";

import { Connection, PublicKey, Transaction, VersionedTransaction } from "@solana/web3.js";
import { useWallet } from "@getpara/react-sdk";
import { useWallet as useSolanaWalletAdapter } from "@solana/wallet-adapter-react";
import { useParaSolana } from "./use-para-solana";
import { useParaAccount } from "./use-appkit-account";
import { useCallback, useMemo } from "react";

export const useParaTransactions = () => {
  const { data: wallet } = useWallet();
  const { wallet: solanaAdapterWallet } = useSolanaWalletAdapter();
  const { signer, connection } = useParaSolana();
  const { address, isExternalSolana } = useParaAccount();
  const blockhashConnection = useMemo(
    () =>
      new Connection(
        typeof window !== "undefined"
          ? `${window.location.origin}/api/rpc?chain=solana`
          : "https://api.mainnet-beta.solana.com",
        { commitment: "confirmed" }
      ),
    []
  );

  const onSendTransaction = useCallback(
    async (transaction: VersionedTransaction | Transaction, maxRetries = 7) => {
      let attempt = 0;
      let signature: string | undefined;

      try {
        // TEMPORARY DIAGNOSTIC - remove after confirming wallet detection works
        if (typeof window !== "undefined") {
          console.log("[wallet diag]", {
            adapter_name: solanaAdapterWallet?.adapter?.name,
            adapter_has_sign: typeof solanaAdapterWallet?.adapter?.signTransaction === "function",
            window_solana_exists: !!(window as any).solana,
            window_solana_isPhantom: (window as any).solana?.isPhantom,
            window_solflare_exists: !!(window as any).solflare,
          });
        }

        const isVersioned = !("feePayer" in transaction);
        let blockhash: string;
        let lastValidBlockHeight: number;

        if (!isVersioned) {
          const latestBlockhash = await blockhashConnection.getLatestBlockhash("confirmed");
          blockhash = latestBlockhash.blockhash;
          lastValidBlockHeight = latestBlockhash.lastValidBlockHeight;

          if (!transaction.feePayer) {
            transaction.feePayer =
              (window as any).solana?.publicKey ??
              (address ? new PublicKey(address) : undefined);
          }

          transaction.recentBlockhash = blockhash;
        } else {
          const latestBlockhash = await blockhashConnection.getLatestBlockhash("confirmed");
          blockhash = latestBlockhash.blockhash;
          lastValidBlockHeight = latestBlockhash.lastValidBlockHeight;
        }

        // Para (email) wallet path - uses dedicated signer
        if (wallet?.type === "SOLANA" && signer && !isExternalSolana) {
          console.log("Signing with Para signer...");
          signature = await signer.sendTransaction(transaction, {
            skipPreflight: false,
            preflightCommitment: "confirmed",
          });
          console.log("✅ Transaction sent via Para signer:", signature);
          return signature;
        }

        // WALLET DETECTION via publicKey matching
        // We check which extension's publicKey matches the CONNECTED wallet's address.
        // This is necessary because window.solana.isPhantom returns true if Phantom is
        // INSTALLED, even if the user connected via Solflare.
        const solanaWallet = (window as any).solana;  // Usually Phantom when both installed
        const solflareWallet = (window as any).solflare;
        const connectedPubkey = address;  // The wallet address the user actually connected

        // Get publicKeys from each extension (if connected)
        const phantomPubkey = solanaWallet?.publicKey?.toString();
        const solflarePubkey = solflareWallet?.publicKey?.toString();

        // Match by publicKey, not by "is installed"
        const isPhantomConnected = !!solanaWallet?.isPhantom && phantomPubkey === connectedPubkey;
        const isSolflareConnected = !!solflareWallet && solflarePubkey === connectedPubkey;

        console.log("[wallet detection]", {
          connectedPubkey,
          phantomPubkey,
          solflarePubkey,
          isPhantomConnected,
          isSolflareConnected,
        });

        // SOLFLARE-SPECIFIC PATH (PRIORITY 1)
        // Check Solflare FIRST because window.solana defaults to Phantom when both are installed.
        // Use signAndSendTransaction to avoid "malicious dApp" warnings.
        if (isSolflareConnected && solflareWallet) {
          console.log("Using Solflare signAndSendTransaction (publicKey match)");

          // Fix feePayer for Solflare
          if (!isVersioned && !transaction.feePayer) {
            transaction.feePayer = solflareWallet.publicKey;
          }

          // Fresh blockhash right before signing
          const { blockhash: freshBlockhash } =
            await blockhashConnection.getLatestBlockhash("confirmed");
          if (!isVersioned) {
            transaction.recentBlockhash = freshBlockhash;
          }

          const result = await solflareWallet.signAndSendTransaction(transaction);
          signature = result.signature;

          if (!signature) {
            throw new Error("Transaction signature was not returned.");
          }

          // Wait for confirmation
          console.log("Confirming Solflare transaction:", signature);
          const MAX_RETRIES = 30;
          const RETRY_DELAY = 2000;

          for (let i = 0; i < MAX_RETRIES; i++) {
            const { value } = await blockhashConnection.getSignatureStatuses([signature]);
            const status = value[0];

            if (
              status?.confirmationStatus === "confirmed" ||
              status?.confirmationStatus === "finalized"
            ) {
              // Transaction landed in a block - check if it succeeded or failed on-chain
              if (status.err) {
                throw new Error(`Transaction failed on-chain: ${JSON.stringify(status.err)}`);
              }
              break;
            }

            if (status?.err) {
              throw new Error(`Transaction failed: ${JSON.stringify(status.err)}`);
            }

            if (i === MAX_RETRIES - 1) {
              throw new Error("Transaction confirmation timeout");
            }

            await new Promise((r) => setTimeout(r, RETRY_DELAY));
          }

          console.log("✅ Solflare transaction confirmed:", signature);
          return signature;
        }

        // PHANTOM-SPECIFIC PATH (PRIORITY 2)
        // Only route to Phantom if its publicKey matches the connected wallet.
        // Use signAndSendTransaction to avoid "malicious dApp" warning.
        if (isPhantomConnected && solanaWallet) {
          console.log("Using Phantom signAndSendTransaction (publicKey match)");

          // Fresh blockhash right before signing
          const { blockhash: freshBlockhash } =
            await blockhashConnection.getLatestBlockhash("confirmed");
          if (!isVersioned) {
            transaction.recentBlockhash = freshBlockhash;
          }

          const result = await solanaWallet.signAndSendTransaction(transaction);
          signature = result.signature;

          if (!signature) {
            throw new Error("Transaction signature was not returned.");
          }

          // Wait for confirmation
          console.log("Confirming Phantom transaction:", signature);
          const MAX_RETRIES = 30;
          const RETRY_DELAY = 2000;

          for (let i = 0; i < MAX_RETRIES; i++) {
            const { value } = await blockhashConnection.getSignatureStatuses([signature]);
            const status = value[0];

            if (
              status?.confirmationStatus === "confirmed" ||
              status?.confirmationStatus === "finalized"
            ) {
              // Transaction landed in a block - check if it succeeded or failed on-chain
              if (status.err) {
                throw new Error(`Transaction failed on-chain: ${JSON.stringify(status.err)}`);
              }
              break;
            }

            if (status?.err) {
              throw new Error(`Transaction failed: ${JSON.stringify(status.err)}`);
            }

            if (i === MAX_RETRIES - 1) {
              throw new Error("Transaction confirmation timeout");
            }

            await new Promise((r) => setTimeout(r, RETRY_DELAY));
          }

          console.log("✅ Phantom transaction confirmed:", signature);
          return signature;
        }

        // GENERIC FALLBACK PATH (PRIORITY 3)
        // For wallets that are NOT Phantom and NOT Solflare (e.g., Backpack, Para email wallets)
        if (!solanaWallet) {
          throw new Error("No Solana wallet detected");
        }

        const hasSignAndSend = typeof solanaWallet.signAndSendTransaction === "function";
        const hasSignOnly = typeof solanaWallet.signTransaction === "function";

        // For non-Phantom wallets, prefer signTransaction (universal) over signAndSendTransaction
        // signTransaction works on: Solflare desktop, Backpack, most extensions
        // signAndSendTransaction fallback for: Solflare in-app browser, some mobile wallets
        if (hasSignOnly) {
          // Universal path: sign with wallet, send via our RPC
          console.log("Using signTransaction + sendRawTransaction (generic wallet path)");
          const signedTransaction = await solanaWallet.signTransaction(transaction);

          while (attempt < maxRetries) {
            attempt++;
            try {
              console.log(`Attempt ${attempt}: sending raw transaction...`);
              signature = await connection.sendRawTransaction(
                signedTransaction.serialize(),
                { skipPreflight: false, preflightCommitment: "confirmed" }
              );

              if (!signature) {
                throw new Error("Transaction signature was not returned.");
              }

              console.log("Confirming transaction:", signature);
              const MAX_RETRIES = 30;
              const RETRY_DELAY = 2000;

              for (let i = 0; i < MAX_RETRIES; i++) {
                const { value } = await blockhashConnection.getSignatureStatuses([signature]);
                const status = value[0];

                if (
                  status?.confirmationStatus === "confirmed" ||
                  status?.confirmationStatus === "finalized"
                ) {
                  // Transaction landed in a block - check if it succeeded or failed on-chain
                  if (status.err) {
                    throw new Error(`Transaction failed on-chain: ${JSON.stringify(status.err)}`);
                  }
                  break;
                }

                if (status?.err) {
                  throw new Error(`Transaction failed: ${JSON.stringify(status.err)}`);
                }

                if (i === MAX_RETRIES - 1) {
                  throw new Error("Transaction confirmation timeout");
                }

                await new Promise((r) => setTimeout(r, RETRY_DELAY));
              }

              console.log("✅ Transaction confirmed:", signature);
              return signature;
            } catch (err: any) {
              const msg = err?.message || err?.toString();
              console.error(`❌ Attempt ${attempt} failed:`, msg);

              // Only retry if it's a transient or blockhash-related error
              if (
                msg.includes("blockhash not found") ||
                msg.includes("BlockhashNotFound") ||
                msg.includes("expired") ||
                msg.includes("Transaction simulation failed") ||
                msg.includes("429") // rate limited
              ) {
                console.warn("⚠️ Retrying send...");
                await new Promise((r) => setTimeout(r, 1000));
                continue;
              }

              throw err; // unrecoverable
            }
          }

          throw new Error(
            "Transaction failed due to network congestion, please try again."
          );
        } else if (hasSignAndSend) {
          // Fallback for wallets that only expose signAndSendTransaction
          // Used by: Solflare in-app browser, some mobile-specific wallets
          console.log("Using signAndSendTransaction (modern-only adapter)");
          const result = await solanaWallet.signAndSendTransaction(transaction);
          signature = result.signature;

          if (!signature) {
            throw new Error("Transaction signature was not returned.");
          }

          // Wait for confirmation
          console.log("Confirming transaction:", signature);
          const MAX_RETRIES = 30;
          const RETRY_DELAY = 2000;

          for (let i = 0; i < MAX_RETRIES; i++) {
            const { value } = await blockhashConnection.getSignatureStatuses([signature]);
            const status = value[0];

            if (
              status?.confirmationStatus === "confirmed" ||
              status?.confirmationStatus === "finalized"
            ) {
              // Transaction landed in a block - check if it succeeded or failed on-chain
              if (status.err) {
                throw new Error(`Transaction failed on-chain: ${JSON.stringify(status.err)}`);
              }
              break;
            }

            if (status?.err) {
              throw new Error(`Transaction failed: ${JSON.stringify(status.err)}`);
            }

            if (i === MAX_RETRIES - 1) {
              throw new Error("Transaction confirmation timeout");
            }

            await new Promise((r) => setTimeout(r, RETRY_DELAY));
          }

          console.log("✅ Transaction confirmed:", signature);
          return signature;
        } else {
          throw new Error("Wallet does not support transaction signing. Please update your wallet app.");
        }
      } catch (err) {
        console.error("Transaction failed:", err);
        throw err;
      }
    },
    [wallet, solanaAdapterWallet, signer, connection, address, isExternalSolana, blockhashConnection]
  );

  return { onSendTransaction };
};
